/**
 * Service API — annulation de la session ordinaire (sanction disciplinaire).
 * Réservé à la Direction et à l'administration.
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithHeaders } from './baseApi';

export type Parite = 'S1' | 'S2';

export interface SanctionSession {
  id: number;
  matricule: string;
  nom: string;
  prenoms: string;
  classe: string;
  niveau: string;
  option: string;
  pole: string;
  annee_academique: string;
  semestre: Parite;
  session: string;
  motif: string;
  motif_display: string;
  details: string;
  reference_decision: string;
  message_portail: string;
  actif: boolean;
  resultats_rattrapage_visibles: boolean;
  cree_par_nom: string;
  date_creation: string | null;
  retire_par_nom: string;
  date_retrait: string | null;
  motif_retrait: string;
}

export interface CandidatSanction {
  id: number;
  matricule: string;
  nom: string;
  prenoms: string;
  classe: string;
  niveau: string;
  option: string;
  pole: string;
  type_formation: string;
  sanction: SanctionSession | null;
}

export interface CandidatsSanctionResponse {
  annee_academique: string;
  semestre: Parite;
  results: CandidatSanction[];
  total: number;
  nb_sanctionnes: number;
}

export interface AppliquerSanctionPayload {
  matricules: string[];
  semestre: Parite;
  motif: string;
  details: string;
  reference_decision: string;
  message_portail: string;
}

export interface OperationSanctionResult {
  message: string;
  crees?: number;
  reactives?: number;
  deja_actives?: number;
  retirees?: number;
  modifiees?: number;
  introuvables?: string[];
  recalcul?: Record<string, unknown>;
}

export const sanctionsApi = createApi({
  reducerPath: 'sanctionsApi',
  baseQuery: baseQueryWithHeaders,
  tagTypes: ['Sanctions'],
  endpoints: (builder) => ({
    getCandidatsSanction: builder.query<CandidatsSanctionResponse, { semestre: Parite }>({
      query: (params) => ({ url: '/sanctions-session/candidats/', params }),
      providesTags: ['Sanctions'],
    }),
    getMessageDefautSanction: builder.query<{ semestre: Parite; message: string }, { semestre: Parite }>({
      query: (params) => ({ url: '/sanctions-session/message-defaut/', params }),
    }),
    appliquerSanction: builder.mutation<OperationSanctionResult, AppliquerSanctionPayload>({
      query: (body) => ({ url: '/sanctions-session/appliquer/', method: 'POST', body }),
      invalidatesTags: ['Sanctions'],
    }),
    retirerSanction: builder.mutation<OperationSanctionResult, { ids: number[]; motif_retrait: string }>({
      query: (body) => ({ url: '/sanctions-session/retirer/', method: 'POST', body }),
      invalidatesTags: ['Sanctions'],
    }),
    visibiliteRattrapage: builder.mutation<OperationSanctionResult, { ids: number[]; visible: boolean }>({
      query: (body) => ({ url: '/sanctions-session/visibilite-rattrapage/', method: 'POST', body }),
      invalidatesTags: ['Sanctions'],
    }),
  }),
});

export const {
  useGetCandidatsSanctionQuery,
  useGetMessageDefautSanctionQuery,
  useAppliquerSanctionMutation,
  useRetirerSanctionMutation,
  useVisibiliteRattrapageMutation,
} = sanctionsApi;
